import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserNextLevel } from '../../api/hooks/User/useUserNextLevel'
import { useUserStep } from '../../api/hooks/User/useUserStep'
import { useAuthStore } from '../../store/useAuthStore'
import { useWrongQuizStore } from '../../store/useWrongQuizStore'
import { useUserGetWrong } from '../../api/hooks/User/useUserGetWrong'
import useRewardStore from '../../store/rewardStore'
import AvatarDisplay from '../individual/AvatarDisplay'
import styles from '../../styles/user/ActivitySummaryNew.module.scss'
import Loading from '../common/Loading'

// Coins/gems earned per wrong-answer review (fixed server-side value from rewardTrigger.js)
const REVIEW_COINS = 20
const REVIEW_GEMS = 5

const ActivitySummaryWrongBox = () => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const updateUserPartial = useAuthStore((state) => state.updateUserPartial)
  const nextLevelMutation = useUserNextLevel()
  const userStepMutation = useUserStep()
  const { userAnswers } = useWrongQuizStore()
  const { coins, gems, avatar, fetchRewardSummary } = useRewardStore()
  const userAvatarSrc = (user as any)?.AVATAR || null

  useEffect(() => {
    if ((user as any)?.USER_ID) fetchRewardSummary((user as any).USER_ID).catch(() => {})
  }, [(user as any)?.USER_ID])

  const { data: wrongData, isLoading } = useUserGetWrong({
    ID: user?.ID || 0,
  })

  // ── handleStartNext: 기존 로직 그대로 유지 ────────────────────
  const handleStartNext = async () => {
    if (!user) return

    const nextStepData = {
      LEVEL_ID: user.LEVEL_ID,
      ID: user.ID,
      STEP: user.STEP,
    }

    try {
      const nextLevelResponse = await nextLevelMutation.mutateAsync(nextStepData)
      const stepResponse = await userStepMutation.mutateAsync({ ID: user.ID })

      let serverStep = null
      if (stepResponse.data && Array.isArray(stepResponse.data)) {
        if (stepResponse.data[0] && stepResponse.data[0].STEP) {
          serverStep = stepResponse.data[0].STEP
        }
      } else if (stepResponse.data && stepResponse.data.STEP) {
        serverStep = stepResponse.data.STEP
      }

      if (serverStep !== null && typeof serverStep === 'number') {
        updateUserPartial({ STEP: serverStep })
        if (serverStep % 5 === 0) {
          navigate('/learningQuizOnly')
        } else {
          navigate('/solver')
        }
      } else {
        navigate('/solver')
      }
    } catch (error) {
      navigate('/solver')
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <Loading />
      </div>
    )
  }

  const actualWrongQuestions = wrongData?.data || []

  // ── 결과 계산 ─────────────────────────────────────────────────
  const totalQuestions = userAnswers.length
  const correctAnswers = userAnswers.filter((a) => a.isCorrect).length
  const wrongCount = totalQuestions - correctAnswers
  const percentage =
    totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0

  const questionData = userAnswers.map((answer, index) => ({
    id: answer.questionId,
    text: `Question ${index + 1}`,
    isCorrect: answer.isCorrect,
  }))

  // SVG semi-circle chart
  const radius = 80
  const strokeWidth = 26
  const cx = 95
  const cy = 95
  const circumference = Math.PI * radius
  const offset = circumference - (percentage / 100) * circumference
  const arcPath = `M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`

  // ── 애니메이션 상태 ────────────────────────────────────────────
  const [animatedOffset, setAnimatedOffset] = useState(circumference)
  const [displayCoins, setDisplayCoins] = useState(0)
  const [displayGems, setDisplayGems] = useState(0)
  const [displayPct, setDisplayPct] = useState(0)

  useEffect(() => {
    const arcTimer = setTimeout(() => setAnimatedOffset(offset), 80)
    const DURATION = 1200
    const STEPS = 36
    let step = 0
    const countTimer = setInterval(() => {
      step++
      const t = step / STEPS
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplayCoins(Math.round(REVIEW_COINS * eased))
      setDisplayGems(Math.round(REVIEW_GEMS * eased))
      setDisplayPct(Math.round(percentage * eased))
      if (step >= STEPS) clearInterval(countTimer)
    }, DURATION / STEPS)
    return () => {
      clearTimeout(arcTimer)
      clearInterval(countTimer)
    }
  }, [])

  return (
    <>
      {/* Coins / Gems overlay */}
      <div className={styles.rewardOverlay}>
        <div className={styles.rewardChip}>
          <img src="/assets/icons/coin.png" alt="coin" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          <span className={styles.rewardChipValue}>{coins.toLocaleString()}</span>
        </div>
        <div className={styles.rewardChip}>
          <img src="/assets/icons/gem.png" alt="gem" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          <span className={styles.rewardChipValue}>{gems}</span>
        </div>
      </div>

      <div className={styles.pageWrapper}>
        {/* Title */}
        <p className={styles.pageTitle}>Activity Summary <img src="/assets/icons/celebration.png" alt="celebration" style={{ width: 24, height: 24, objectFit: 'contain', verticalAlign: 'middle' }} /></p>
        <p className={styles.pageSubtitle}>Great job! Keep up the amazing work!</p>

        {/* Reward section */}
        <div className={styles.rewardSection}>
          <img
            src="/assets/summary-char-left.png"
            alt=""
            className={styles.rewardLeftChar}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
          <div className={styles.rewardCard}>
            <div className={styles.rewardItem}>
              <div className={styles.rewardIconCircle} style={{ background: '#fff8e6' }}>
                <img src="/assets/icons/coin.png" alt="coin" style={{ width: 36, height: 36, objectFit: 'contain' }} />
              </div>
              <div className={styles.rewardItemInfo}>
                <span className={styles.rewardItemLabel}>Coins Earned</span>
                <span className={styles.rewardItemValue}>+{displayCoins}</span>
              </div>
            </div>
            <div className={styles.rewardDivider} />
            <div className={styles.rewardItem}>
              <div className={styles.rewardIconCircle} style={{ background: '#f3eeff' }}>
                <img src="/assets/icons/gem.png" alt="gem" style={{ width: 36, height: 36, objectFit: 'contain' }} />
              </div>
              <div className={styles.rewardItemInfo}>
                <span className={styles.rewardItemLabel}>Gems Earned</span>
                <span className={styles.rewardItemValue}>+{displayGems}</span>
              </div>
            </div>
          </div>
          <img
            src="/assets/summary-chest-right.png"
            alt=""
            className={styles.rewardRightChest}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        </div>

        {/* Main card */}
        <div className={styles.mainCard}>
          {/* Left: character + username */}
          <div className={styles.charColumnWrapper}>
            <div className={styles.charColumn}>
              <AvatarDisplay
                avatar={{ ...avatar, background: null }}
                userAvatar={userAvatarSrc}
                size={185}
                showSparkles={false}
                naked={true}
                onClick={undefined}
              />
            </div>
            <span className={styles.charUsername}>
              {user?.NICKNAME || user?.NAME || 'Student'}
            </span>
          </div>

          {/* Center: wrong review topic + question list */}
          <div className={styles.topicColumn}>
            <p className={styles.topicLabel}>Lesson Topic</p>
            <p className={styles.topicTitle}>Wrong Answer Review</p>

            {actualWrongQuestions.length === 0 ? (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#1f9d55',
                  fontWeight: 700,
                  fontSize: '15px',
                }}
              >
                <img src="/assets/icons/celebration.png" alt="celebration" style={{ width: 20, height: 20, objectFit: 'contain', verticalAlign: 'middle', marginRight: 6 }} />Perfect! No incorrect answers to review.
              </div>
            ) : (
              <div className={styles.questionListWrapper}>
                {questionData.map((q) => (
                  <div key={q.id} className={styles.questionRow}>
                    <span className={styles.questionText}>{q.text}</span>
                    {q.isCorrect ? (
                      <span className={styles.correctBadge}>Correct</span>
                    ) : (
                      <span className={styles.wrongBadge}>Wrong</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: progress chart */}
          <div className={styles.progressColumn}>
            <p className={styles.progressLabel}>Overall Progress</p>
            <div className={styles.percentageBadge}>
              <span className={styles.percentageValue}>{displayPct}%</span>
              <span className={styles.percentageCountLine}>
                {correctAnswers} / {totalQuestions} Questions Correct
              </span>
            </div>
            <svg
              width="190"
              height="105"
              viewBox="0 0 190 105"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              overflow="visible"
            >
              <defs>
                <linearGradient id="arcGradW" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4f72f5" />
                  <stop offset="100%" stopColor="#2540c8" />
                </linearGradient>
              </defs>

              {/* 트랙 */}
              <path d={arcPath} stroke="#e8eaf0" strokeWidth={strokeWidth} strokeLinecap="round" fill="none" />

              {/* 채움 */}
              <path
                d={arcPath}
                stroke="url(#arcGradW)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={animatedOffset}
                fill="none"
                className={styles.arcAnimated}
              />
            </svg>
          </div>
        </div>

        {/* Bottom stats — unified card */}
        <div className={styles.statsCard}>
          <div className={styles.statSection}>
            <div className={styles.statIconCircle} style={{ background: '#eef0ff' }}>
              <img src="/assets/icons/target.png" alt="accuracy" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>Accuracy</span>
              <span className={styles.statValue} style={{ color: '#4361ee' }}>
                {percentage}%
              </span>
              <span className={styles.statSub}>
                {correctAnswers} Correct / {totalQuestions} Total
              </span>
            </div>
          </div>

          <div className={styles.statsDivider} />

          <div className={styles.statSection}>
            <div className={styles.statIconCircle} style={{ background: '#e8f8ef' }}>
              <img src="/assets/icons/check.png" alt="correct" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>Correct</span>
              <span className={styles.statValue} style={{ color: '#2d9e5c' }}>
                {correctAnswers}
              </span>
              <span className={styles.statSub}>Questions</span>
            </div>
          </div>

          <div className={styles.statsDivider} />

          <div className={styles.statSection}>
            <div className={styles.statIconCircle} style={{ background: '#fde8e8' }}>
              <img src="/assets/icons/wrong.png" alt="wrong" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>Wrong</span>
              <span className={styles.statValue} style={{ color: '#e53e3e' }}>
                {wrongCount}
              </span>
              <span className={styles.statSub}>Questions</span>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className={styles.buttonRow}>
          <button
            className={styles.navyBtn}
            onClick={() => navigate('/dashboard/individual')}
          >
            Back to Dashboard
          </button>
          <button className={styles.orangeBtn} onClick={handleStartNext}>
            Start Next Activity! →
          </button>
        </div>
      </div>
    </>
  )
}

export default ActivitySummaryWrongBox
